"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { format, addDays, isBefore, startOfDay, parseISO } from "date-fns";
import { Calendar, Clock, CheckCircle, ArrowLeft, ChevronLeft, ChevronRight, Loader2, MessageCircle, Info } from "lucide-react";
import { API_ENDPOINTS } from "@/constants/config";
import toast from "react-hot-toast";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildCalendarDays(baseDate) {
  const today = startOfDay(new Date());
  const days = [];
  for (let i = 0; i < 90; i++) {
    const d = addDays(today, i);
    days.push({ date: d, label: format(d, "yyyy-MM-dd"), disabled: isBefore(d, today) });
  }
  return days;
}

function formatSlot(slot) {
  const [h, m] = slot.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

export default function ScheduleDemo() {
  const today = startOfDay(new Date());
  const [calendarStart, setCalendarStart] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState("date"); // date | slot | form | success
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    schoolName: "",
    city: "",
    designation: "",
    organizationType: "",
    numberOfStudents: "",
    hasSchoolBuses: false,
    numberOfBuses: "",
    featuresToManage: [],
    specificRequirements: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  // Feature options with tooltip info (shown when hovering the small (i) icon)
  const featureOptions = [
    { value: "Student Attendance Tracking", label: "Student Attendance Tracking", info: "mark attendance, reports, alerts" },
    { value: "School Transport & Bus Tracking", label: "School Transport & Bus Tracking", info: "routes, buses, safety monitoring" },
    { value: "Fee Management & Collection", label: "Fee Management & Collection", info: "payments, dues, receipts, reminders" },
    { value: "Inventory & Asset Management", label: "Inventory & Asset Management", info: "stock, items, school resources" },
    { value: "Exams & Report Cards", label: "Exams & Report Cards", info: "marks entry, results, analytics" },
    { value: "Timetable & Scheduling", label: "Timetable & Scheduling", info: "class planning, teacher allocation" },
    { value: "Homework & Assignments", label: "Homework & Assignments", info: "upload, track, student submissions" },
    { value: "Parent App & Communication", label: "Parent App & Communication", info: "notifications, updates, messaging" },
    { value: "Staff & Payroll Management", label: "Staff & Payroll Management", info: "teacher data, salary, attendance" },
    { value: "Admissions & Enquiry Management", label: "Admissions & Enquiry Management", info: "new student leads, follow-ups" },
    { value: "Student Information System", label: "Student Information System", info: "complete student records & profiles" },
    { value: "Library Management System", label: "Library Management System", info: "books, issue/return, fines" },
    { value: "Accounts & Financial Management", label: "Accounts & Financial Management", info: "income, expenses, reports" },
    { value: "Reports & Analytics Dashboard", label: "Reports & Analytics Dashboard", info: "data insights, performance tracking" },
    { value: "Certificates & ID Management", label: "Certificates & ID Management", info: "ID cards, transfer certificates, documents" },
    { value: "Visitor & Security Management", label: "Visitor & Security Management", info: "gate entry, safety tracking" },
    { value: "Leave & Staff Attendance Management", label: "Leave & Staff Attendance Management", info: "leave requests, approvals" },
    { value: "Events & School Calendar", label: "Events & School Calendar", info: "holidays, activities, scheduling" },
  ];

  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!selectAllRef.current) return;
    const len = form.featuresToManage.length;
    if (len === 0) {
      selectAllRef.current.indeterminate = false;
    } else if (len === featureOptions.length) {
      selectAllRef.current.indeterminate = false;
    } else {
      selectAllRef.current.indeterminate = true;
    }
  }, [form.featuresToManage]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setForm((f) => ({ ...f, featuresToManage: featureOptions.map((o) => o.value) }));
    } else {
      setForm((f) => ({ ...f, featuresToManage: [] }));
    }
  };

  const toggleFeature = (val) => {
    setForm((f) => {
      const exists = f.featuresToManage.includes(val);
      return { ...f, featuresToManage: exists ? f.featuresToManage.filter((x) => x !== val) : [...f.featuresToManage, val] };
    });
  };

  const visibleDays = buildCalendarDays(today).slice(
    Math.floor((calendarStart - today) / 86400000),
    Math.floor((calendarStart - today) / 86400000) + 14
  );

  // Build 14-day window from calendarStart
  const calendarDays = (() => {
    const todayMs = today.getTime();
    const startOffset = Math.round((calendarStart.getTime() - todayMs) / 86400000);
    const all = buildCalendarDays(today);
    return all.slice(startOffset, startOffset + 14);
  })();

  const canGoPrev = calendarStart > today;
  const canGoNext = addDays(calendarStart, 14) < addDays(today, 90);

  const fetchSlots = useCallback(async (dateLabel) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const res = await fetch(API_ENDPOINTS.publicDemoSlots(dateLabel));
      const json = await res.json();
      if (json.status === "success") {
        let fetchedSlots = json.data.slots;
        
        // Filter out past slots if today's date is selected
        const selectedDateObj = new Date(dateLabel);
        const isToday = format(selectedDateObj, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
        
        if (isToday) {
          const now = new Date();
          const currentHour = now.getHours();
          const currentMinutes = now.getMinutes();
          
          fetchedSlots = fetchedSlots.filter(slot => {
            const [slotHour, slotMinutes] = slot.slot.split(":").map(Number);
            return slotHour > currentHour || (slotHour === currentHour && slotMinutes > currentMinutes);
          });
        }
        
        setSlots(fetchedSlots);
      }
    } catch {
      toast.error("Failed to load time slots");
    } finally {
      setSlotsLoading(false);
    }
  }, [today]);

  const handleDateSelect = (day) => {
    if (day.disabled) return;
    setSelectedDate(day);
    setSelectedSlot(null);
    fetchSlots(day.label);
    setStep("slot");
  };

  const handleSlotSelect = (s) => {
    if (!s.available) return;
    setSelectedSlot(s.slot);
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.schoolName ||
      !form.designation ||
      !form.organizationType ||
      !form.numberOfStudents
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    // Client-side email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setEmailError("Please enter a valid email address");
      toast.error("Please enter a valid email address");
      return;
    }

    // Client-side phone validation (exactly 10 digits)
    const phoneDigits = String(form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      setPhoneError("Please enter a valid 10-digit phone number");
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    // Validate numberOfStudents
    if (Number(form.numberOfStudents) <= 0) {
      toast.error("Number of students must be greater than 0");
      return;
    }

    // Validate numberOfBuses if hasSchoolBuses is true
    if (form.hasSchoolBuses && (!form.numberOfBuses || Number(form.numberOfBuses) <= 0)) {
      toast.error("Please enter a valid number of buses");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.publicDemoBook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          numberOfStudents: Number(form.numberOfStudents),
          numberOfBuses: form.hasSchoolBuses ? Number(form.numberOfBuses) : 0,
          date: selectedDate.label,
          timeSlot: selectedSlot,
        }),
      });
      const json = await res.json();
      if (json.status === "success") {
        setStep("success");
      } else {
        toast.error(json.message || "Booking failed");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const monthLabel = (() => {
    const first = calendarDays[0]?.date;
    const last = calendarDays[calendarDays.length - 1]?.date;
    if (!first || !last) return "";
    if (first.getMonth() === last.getMonth()) {
      return `${MONTHS[first.getMonth()]} ${first.getFullYear()}`;
    }
    return `${MONTHS[first.getMonth()]} – ${MONTHS[last.getMonth()]} ${last.getFullYear()}`;
  })();

  if (step === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Demo Booked!</h2>
          <p className="text-gray-500 mb-2">
            Your demo is scheduled for <span className="font-semibold text-gray-800">{format(parseISO(selectedDate.label), "EEEE, MMMM d, yyyy")}</span> at{" "}
            <span className="font-semibold text-gray-800">{formatSlot(selectedSlot)}</span>.
          </p>
          <p className="text-gray-500 mb-8 text-sm">
            Our team will reach out to confirm. You'll receive a call on <span className="font-medium">{form.phone}</span>.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                  const namePart = form.name ? `${form.name}` : "";
                  const schoolPart = form.schoolName ? ` from ${form.schoolName}` : "";
                  const message = `Hi, I${namePart ? `, ${namePart}` : ""}${schoolPart} just booked a demo for ${format(parseISO(selectedDate.label), "MMMM d, yyyy")} at ${formatSlot(selectedSlot)}.`;
                  const whatsappUrl = `https://wa.me/917499070944?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, "_blank");
                }}
              className="w-full py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Message on WhatsApp
            </button>
            <button
              onClick={() => {
                setStep("date");
                setSelectedDate(null);
                setSelectedSlot(null);
                setForm({
                  name: "",
                  email: "",
                  phone: "",
                  schoolName: "",
                  city: "",
                  designation: "",
                  organizationType: "",
                  numberOfStudents: "",
                  hasSchoolBuses: false,
                  numberOfBuses: "",
                  featuresToManage: [],
                  specificRequirements: "",
                });
              }}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Book Another Demo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <Calendar className="w-4 h-4" />
            Schedule a Demo
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            See Clynox in Action
          </h1>
          <p className="text-gray-500 max-w-md mx-auto">
            Book a free 30-minute live demo with our team. Pick a date and time that works for you.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5">

            {/* Left Panel, Info */}
            <div className="lg:col-span-2 bg-blue-600 text-white p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <img src="/logo-no-bg.png" alt="Clynox" className="h-8 brightness-0 invert" onError={(e) => { e.target.style.display = 'none'; }} />
                  <span className="text-xl font-bold">Clynox</span>
                </div>
                <h2 className="text-2xl font-bold mb-2">30-Minute Demo</h2>
                <p className="text-blue-200 text-sm mb-8 leading-relaxed">
                  Get a personalised walkthrough of Clynox ERP, attendance, timetable, fee management, bus tracking, and more.
                </p>

                <div className="space-y-4">
                  {[
                    { icon: Clock, text: "30 minutes · Online" },
                    { icon: Calendar, text: "Mon – Sat, 10 AM – 10 PM" },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3 text-sm text-blue-100">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      {text}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step breadcrumb */}
              <div className="mt-10">
                <div className="flex items-center gap-2">
                  {["date", "slot", "form"].map((s, i) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                        step === s ? "bg-white text-blue-600 border-white" :
                        ["date","slot","form"].indexOf(step) > i ? "bg-blue-400 border-blue-400 text-white" :
                        "border-blue-400 text-blue-300"
                      }`}>
                        {["date","slot","form"].indexOf(step) > i ? "✓" : i + 1}
                      </div>
                      <span className={`text-xs ${step === s ? "text-white font-medium" : "text-blue-300"}`}>
                        {s === "date" ? "Date" : s === "slot" ? "Time" : "Details"}
                      </span>
                      {i < 2 && <div className="w-4 h-px bg-blue-400" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="lg:col-span-3 p-8">

              {/* Step: Pick Date */}
              {step === "date" && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Select a Date</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCalendarStart((d) => addDays(d, -14))}
                        disabled={!canGoPrev}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="text-sm font-medium text-gray-600 min-w-[160px] text-center">{monthLabel}</span>
                      <button
                        onClick={() => setCalendarStart((d) => addDays(d, 14))}
                        disabled={!canGoNext}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 mb-2">
                    {DAYS.map((d) => (
                      <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
                    ))}
                  </div>

                  {/* Date grid, fill leading blanks */}
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const items = [];
                      const firstDay = calendarDays[0]?.date;
                      const leadingBlanks = firstDay ? firstDay.getDay() : 0;
                      for (let i = 0; i < leadingBlanks; i++) {
                        items.push(<div key={`blank-${i}`} />);
                      }
                      calendarDays.forEach((day) => {
                        const isToday = day.label === format(today, "yyyy-MM-dd");
                        items.push(
                          <button
                            key={day.label}
                            disabled={day.disabled}
                            onClick={() => handleDateSelect(day)}
                            className={`aspect-square rounded-xl text-sm font-medium transition-all flex items-center justify-center ${
                              day.disabled
                                ? "text-gray-300 cursor-not-allowed"
                                : isToday
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                            }`}
                          >
                            {day.date.getDate()}
                          </button>
                        );
                      });
                      return items;
                    })()}
                  </div>
                </div>
              )}

              {/* Step: Pick Slot */}
              {step === "slot" && (
                <div>
                  <button onClick={() => setStep("date")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back to dates
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a Time</h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {format(parseISO(selectedDate.label), "EEEE, MMMM d, yyyy")} · IST
                  </p>

                  {slotsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s.slot}
                          disabled={!s.available}
                          onClick={() => handleSlotSelect(s)}
                          className={`py-2.5 px-2 rounded-xl text-sm font-medium text-center border transition-all ${
                            !s.available
                              ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                              : "border-blue-200 text-blue-700 hover:bg-blue-600 hover:text-white hover:border-blue-600"
                          }`}
                        >
                          {formatSlot(s.slot)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step: Form */}
              {step === "form" && (
                <div>
                  <button onClick={() => setStep("slot")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Change time
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Your Details</h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {format(parseISO(selectedDate.label), "MMMM d, yyyy")} · {formatSlot(selectedSlot)} IST
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Rajesh Kumar"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({ ...f, email: v }));
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (v === "") setEmailError("");
                            else if (!emailRegex.test(v)) setEmailError("Please enter a valid email address");
                            else setEmailError("");
                          }}
                          placeholder="principal@school.com"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                        <input
                          type="tel"
                          required
                          value={form.phone}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({ ...f, phone: v }));
                            const digits = String(v || "").replace(/\D/g, "");
                            if (digits.length === 0) setPhoneError("");
                            else if (digits.length !== 10) setPhoneError("Please enter exactly 10 digits");
                            else setPhoneError("");
                          }}
                          placeholder="+91 98765 43210"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">School Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={form.schoolName}
                          onChange={(e) => setForm((f) => ({ ...f, schoolName: e.target.value }))}
                          placeholder="Delhi Public School"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                          placeholder="New Delhi"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Designation <span className="text-red-500">*</span></label>
                        <select
                          required
                          value={form.designation}
                          onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Designation</option>
                          <option value="Owner">Owner</option>
                          <option value="Principal">Principal</option>
                          <option value="Admin">Admin</option>
                          <option value="Manager">Manager</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Type <span className="text-red-500">*</span></label>
                        <select
                          required
                          value={form.organizationType}
                          onChange={(e) => setForm((f) => ({ ...f, organizationType: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Organization Type</option>
                          <option value="School">School</option>
                          <option value="Coaching">Coaching</option>
                          <option value="Business">Business</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Students <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          required
                          min="1"
                          value={form.numberOfStudents}
                          onChange={(e) => setForm((f) => ({ ...f, numberOfStudents: e.target.value }))}
                          placeholder="500"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Do you have school buses? <span className="text-red-500">*</span></label>
                        <div className="flex gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="buses"
                              checked={form.hasSchoolBuses === false}
                              onChange={() => setForm((f) => ({ ...f, hasSchoolBuses: false, numberOfBuses: "" }))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-700">No</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="buses"
                              checked={form.hasSchoolBuses === true}
                              onChange={() => setForm((f) => ({ ...f, hasSchoolBuses: true }))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-700">Yes</span>
                          </label>
                        </div>
                      </div>
                      {form.hasSchoolBuses && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Buses <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            required
                            min="1"
                            value={form.numberOfBuses}
                            onChange={(e) => setForm((f) => ({ ...f, numberOfBuses: e.target.value }))}
                            placeholder="5"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Features to Manage <span className="text-red-500">*</span></label>

                        {/* Select All / All-in-one top option */}
                        <div className="mb-3">
                          <label className="flex items-center gap-3 cursor-pointer bg-gray-50 p-3 rounded-lg">
                            <input
                              ref={selectAllRef}
                              type="checkbox"
                              checked={form.featuresToManage.length === featureOptions.length}
                              onChange={handleSelectAll}
                              className="w-4 h-4 rounded"
                            />
                            <div className="flex-1 text-sm font-medium text-gray-800">Complete School Management System (All-in-one)</div>
                            <div className="relative group flex-shrink-0">
                              <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
                                <Info className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="absolute -top-1 right-6 w-64 p-2 bg-gray-800 text-white text-xs rounded hidden group-hover:block z-30">
                                On selecting this, everything gets selected.
                              </div>
                            </div>
                          </label>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {featureOptions.map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.featuresToManage.includes(opt.value)}
                                onChange={() => toggleFeature(opt.value)}
                                className="w-4 h-4 rounded"
                              />
                              <span className="text-sm text-gray-700">{opt.label}</span>
                              <div className="relative group">
                                <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center ml-2">
                                  <Info className="w-3 h-3 text-blue-500" />
                                </div>
                                <div className="absolute left-6 -top-1 mt-2 w-56 p-2 bg-gray-800 text-white text-xs rounded hidden group-hover:block z-30">
                                  {opt.info}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Any specific requirement/query (optional)</label>
                        <textarea
                          value={form.specificRequirements}
                          onChange={(e) => setForm((f) => ({ ...f, specificRequirements: e.target.value }))}
                          placeholder="Tell us about your specific needs..."
                          rows="3"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-70 transition-colors flex items-center justify-center gap-2 mt-2"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? "Booking..." : "Confirm Demo Booking"}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      By booking, you agree to be contacted by Clynox team.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
